<tui-loader
	size="l"
	[loading]="loading"
	[overlay]="true"
	[textContent]="
		loading
			? ('analyses.dialog.create-analysis.parsing' | transloco)
			: undefined
	"
>
	<section tuiContent>
		@if (form) {
			<form [formGroup]="form" class="gap-10">
				<div class="flex gap-10">
					<div class="flex flex-col gap-2 w-0 flex-1 max-w-sm">
						<p class="label">
							{{
								'analyses.dialog.create-analysis.upload-csv'
									| transloco
							}}
						</p>

						<span [class.hidden]="form.get('file')?.value">
							<label tuiInputFiles>
								<input
									#validator="tuiInputFilesValidator"
									tuiInputFiles
									[accept]="acceptedFiles"
									[maxFileSize]="acceptedFileMaxSize"
									formControlName="file"
								/>

								<ng-template>
									<div
										class="content flex items-center h-11 gap-3"
									>
										<tui-icon
											class="text-primary flex-grow-0 flex-shrink-0"
											size="xs"
											icon="@tui.upload"
										/>

										<p
											class="text-primary text-sm font-semibold text-center flex-1"
										>
											{{
												'analyses.dialog.create-analysis.action.upload-csv'
													| transloco
											}}
										</p>
									</div>
								</ng-template>
							</label>
						</span>

						<tui-files>
							@if (form.get('file')?.value; as file) {
								<tui-file
									*tuiItem
									[leftContent]="idle"
									[file]="
										(file
											| tuiFileRejected: validator
											| async) || file
									"
									(remove)="onRemoveFile()"
								/>

								<ng-template #idle>
									<tui-icon icon="@tui.clock" />
								</ng-template>
							}
						</tui-files>

						<tui-error
							[order]="['required']"
							formControlName="file"
						/>
					</div>

					@if (hasValidFile) {
						<label tuiLabel class="w-0 flex-1">
							{{
								'analyses.dialog.create-analysis.name-analysis'
									| transloco
							}}

							<tui-textfield tuiTextfieldSize="m">
								<input
									[placeholder]="
										'analyses.dialog.create-analysis.name-placeholder'
											| transloco
									"
									formControlName="name"
									tuiInput
									type="text"
								/>
							</tui-textfield>

							<tui-error
								[order]="['required']"
								formControlName="name"
							/>
						</label>
					}
				</div>

				@if (hasValidFile) {
					<label tuiLabel class="w-full max-w-56">
						{{ 'currency.label' | transloco }}

						<tui-textfield
							tuiTextfieldSize="m"
							tuiChevron
							[tuiTextfieldCleaner]="false"
							[content]="currencyContent"
						>
							<input
								tuiSelect
								formControlName="currency"
								[placeholder]="
									'currency.placeholder' | transloco
								"
							/>

							<tui-data-list-wrapper
								new
								*tuiDropdown
								[items]="currencies"
								[itemContent]="currencyContent"
							/>

							<ng-template #currencyContent let-value>
								{{ 'currency.' + value | transloco }}
							</ng-template>
						</tui-textfield>

						<tui-error
							[order]="['required']"
							formControlName="currency"
						/>
					</label>

					@if (timePeriods) {
						<p class="max-w-lg">
							{{
								'analyses.dialog.create-analysis.time-period.description'
									| transloco
							}}
						</p>
						@for (time of timePeriods.controls; track time) {
							@if (timePeriods.at($index); as formGroup) {
								<div
									class="flex gap-10"
									[formGroup]="$any(formGroup)"
								>
									<!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
									<label tuiLabel class="flex-1">
										{{
											'analyses.dialog.create-analysis.time-period.min-date'
												| transloco
													: { count: $index + 1 }
										}}

										<tui-textfield tuiTextfieldSize="m">
											<input
												tuiInputDate
												formControlName="minDate"
												[max]="
													formGroup.get('maxDate')
														?.value ?? maxDate
												"
												[min]="
													$first
														? null
														: (timePeriods
																.at($index - 1)
																.get('maxDate')
																?.value ??
															minDate)
												"
												placeholder="{{
													'analyses.dialog.create-analysis.time-period.date-placeholder'
														| transloco
												}}"
											/>
											<tui-calendar *tuiDropdown />
										</tui-textfield>

										<tui-error
											[order]="['required']"
											formControlName="minDate"
										/>
									</label>

									<!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
									<label tuiLabel class="flex-1">
										{{
											'analyses.dialog.create-analysis.time-period.max-date'
												| transloco
													: { count: $index + 1 }
										}}

										<tui-textfield tuiTextfieldSize="m">
											<input
												tuiInputDate
												formControlName="maxDate"
												[min]="
													formGroup.get('minDate')
														?.value ?? minDate
												"
												[max]="
													$last
														? null
														: (timePeriods
																.at($index + 1)
																.get('minDate')
																?.value ??
															maxDate)
												"
												placeholder="{{
													'analyses.dialog.create-analysis.time-period.date-placeholder'
														| transloco
												}}"
											/>
											<tui-calendar *tuiDropdown />
										</tui-textfield>

										<tui-error
											[order]="['required']"
											formControlName="maxDate"
										/>
									</label>

									<!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
									<label tuiLabel class="w-full flex-1">
										{{
											'analyses.dialog.create-analysis.time-period.overhead-costs'
												| transloco
													: { count: $index + 1 }
										}}

										<tui-textfield tuiTextfieldSize="m">
											<input
												tuiInputNumber
												formControlName="overheadCosts"
												[placeholder]="
													'analyses.dialog.create-analysis.time-period.cost-placeholder'
														| transloco
												"
												[tuiNumberFormat]="{
													thousandSeparator: ',',
												}"
												[min]="0"
												[prefix]="
													form.get('currency')?.value
														| currencySymbol
												"
											/>
										</tui-textfield>

										<tui-error
											[order]="['required']"
											formControlName="overheadCosts"
										/>
									</label>
								</div>
							}
						}
					}

					<label tuiLabel class="w-full max-w-56">
						{{
							'analyses.dialog.create-analysis.cost-type.label'
								| transloco
						}}

						<tui-textfield
							tuiTextfieldSize="m"
							tuiChevron
							[tuiTextfieldCleaner]="false"
							[content]="content"
						>
							<input
								tuiSelect
								formControlName="costType"
								[placeholder]="
									'analyses.dialog.create-analysis.cost-type.placeholder'
										| transloco
								"
							/>

							<tui-data-list-wrapper
								new
								*tuiDropdown
								[items]="costTypes"
								[itemContent]="content"
							/>

							<ng-template #content let-value>
								{{
									'analyses.dialog.create-analysis.cost-type.' +
										value | transloco
								}}
							</ng-template>
						</tui-textfield>

						<tui-error
							[order]="['required']"
							formControlName="costType"
						/>
					</label>

					<!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
					<label tuiLabel class="flex-1 max-w-56">
						<span class="flex gap-1 items-center">
							{{
								'analyses.dialog.create-analysis.expires-at'
									| transloco
							}}

							<tui-icon
								tabindex="0"
								class="mt-0.5"
								size="xxs"
								icon="@tui.info"
								[tuiHint]="
									'analyses.dialog.create-analysis.expires-at-hint'
										| transloco
								"
								tuiHintDescribe
							/>
						</span>

						<tui-textfield tuiTextfieldSize="m">
							<input tuiInputDate formControlName="expiresAt" />
							<tui-calendar *tuiDropdown />
						</tui-textfield>

						<tui-error
							[order]="['required']"
							formControlName="expiresAt"
						/>
					</label>
				}
			</form>
		}
	</section>

	<footer tuiFooter>
		<button tuiButton size="s" appearance="outline" (click)="onDismiss()">
			{{ 'action.cancel' | transloco }}
		</button>

		<button
			tuiButton
			size="s"
			type="button"
			[disabled]="form?.invalid"
			(click)="onSubmit()"
			[loading]="loading"
		>
			{{ 'analyses.action.start-analysis' | transloco }}
		</button>
	</footer>
</tui-loader>

@if (uploadProgress$ | async; as progress) {
	@if (progress.uploading || progress.complete) {
		<div
			class="absolute bg-white inset-0 flex items-center justify-center h-full w-full z-10 flex-col gap-2 py-8"
		>
			<span class="absolute inset-0 bottom-auto">
				<div tuiNotification appearance="warning">
					{{ 'generic.upload-warning' | transloco }}
				</div>
			</span>

			@if (progress.complete) {
				<h1 class="text-xl font-bold">
					{{ 'generic.upload-complete' | transloco }}
				</h1>
			} @else {
				<h1 class="text-xl font-bold">
					{{ 'generic.uploading' | transloco }}
				</h1>
			}

			<progress
				class="bg-primary-1000 max-w-sm mt-4"
				max="100"
				tuiProgressBar
				[value]="progress.value"
			></progress>

			<p class="text-sm">{{ progress.value }}%</p>
		</div>
	}
}
